import type { Scope, TransactionEvent, ErrorEvent, EventHint, Exception } from '@sentry/core';
import { getToken } from '@/util/jwtUtil';
import type { Breadcrumb, BreadcrumbHint, init } from '@sentry/vue';
import { KycSessionStorage } from '@/common/KycSessionStorage';
import type { KycSessionData } from '@/stores/auth.store';
import { jwtDecode } from 'jwt-decode';
import { config } from '@/config';
import { Liveness } from '@/common/types';
import { ImmersveError } from '@/common/ImmersveError';
import { isEnvironmentalError } from '@/util/sentry/environmentalErrors';
import axios from 'axios';

export function exists<T>(value: T | null | undefined): value is T {
  return value === (value ?? !value);
}

// Strip session IDs from URLs so errors with per-session URLs group into one Sentry issue.
// Immersve session IDs are randomBytes(16).toString('hex') => exactly 32 lowercase hex chars.
function normalizeForFingerprint(value: string): string {
  return value
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '*')
    .replace(/\b[0-9a-f]{32}\b/g, '*');
}

const errorTypesToNormalizeValue = ['UnhandledIDVException', 'UnhandledKycSessionException'];

export function getValueForException(value: Exception): string | undefined {
  const errorTypesToSplitByValue = [
    'AxiosError',
    'SumsubWebSdkFailed',
    'FrankieOneSdkFailed',
    'UnhandledIDVException',
    'UnhandledKycSessionException',
    'FrankieOneIdvInitializationError',
  ];

  if (value.type && errorTypesToSplitByValue.includes(value.type) && value.value) {
    const fingerprintValue = errorTypesToNormalizeValue.includes(value.type)
      ? normalizeForFingerprint(value.value)
      : value.value;
    return `${value.type}: ${fingerprintValue}`;
  }

  return value.type;
}

export interface KycSessionToken {
  data: KycData | undefined;
}

export interface KycData {
  entityId: string | undefined;
}

// Auth-token redemption failing with a 401 means the auth/refresh code has
// expired or already been used (e.g. the user reopened or reloaded the KYC
// link). That is an expected condition that redirects to the timeout screen,
// not an application error, so it should not be reported to Sentry.
const AUTH_TOKEN_ERROR_NAMES = ['RedeemAuthCodeFailed', 'RedeemRefreshTokenFailed'];

function isExpiredAuthToken(error: ImmersveError): boolean {
  if (!AUTH_TOKEN_ERROR_NAMES.includes(error.name)) {
    return false;
  }
  const { cause } = error;
  if (!axios.isAxiosError(cause)) {
    return false;
  }
  return (cause.response?.status ?? cause.status) === 401;
}

export function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  // Timeout / network errors are environmental noise driven by the user's
  // connectivity, not app bugs, so drop them rather than alerting on them.
  if (isEnvironmentalError(event)) {
    return null;
  }
  if (hint.originalException instanceof ImmersveError) {
    if (isExpiredAuthToken(hint.originalException)) {
      return null;
    }
    if (event.exception?.values) {
      const exceptionTypes = event.exception.values
        .map((value) => getValueForException(value))
        .filter(exists);
      event.fingerprint = exceptionTypes;
    }
  }
  return event;
}

// Traces we never want to sample (matched against the span name exactly).
const TRACE_NAMES_TO_DROP = [
  '/fluorite/favicon.ico',
  'sentry-tracing-init',
  '<unknown>',
  'translate:initiated',
  'Timeout',
  'https://verify.immersve.com/timeout',
];

// Traces we never want to sample (matched as a substring of the span name).
const TRACE_NAME_FRAGMENTS_TO_DROP = ['/mixpanel/'];

// Noisy third-party traces we keep, but only a small sample of.
const REDUCED_SAMPLE_RATES = [
  { fragment: 'maps.googleapis.com', rate: 0.1 },
  { fragment: 'gstatic.com', rate: 0.1 },
];

const DEFAULT_TRACE_SAMPLE_RATE = 0.25;

// The context object Sentry passes to `tracesSampler` carries the v9
// `inheritOrSampleWith` helper, but its type is not exported publicly, so we
// derive it from the `init` options.
type TracesSamplerContext = Parameters<
  NonNullable<NonNullable<Parameters<typeof init>[0]>['tracesSampler']>
>[0];

export function tracesSampler({ name, inheritOrSampleWith }: TracesSamplerContext): number {
  if (TRACE_NAMES_TO_DROP.includes(name)) {
    return 0;
  }

  if (TRACE_NAME_FRAGMENTS_TO_DROP.some((fragment) => name.includes(fragment))) {
    return 0;
  }

  const reduced = REDUCED_SAMPLE_RATES.find(({ fragment }) => name.includes(fragment));
  if (reduced) {
    return reduced.rate;
  }

  // Inherit an upstream trace's sampling decision when present (keeping
  // distributed traces intact and metric extrapolation accurate); otherwise
  // sample at the default rate.
  return inheritOrSampleWith(DEFAULT_TRACE_SAMPLE_RATE);
}

export function beforeSendTransaction(
  event: TransactionEvent
): TransactionEvent | PromiseLike<TransactionEvent | null> | null {
  if (event.contexts?.trace?.data?.query?.token) {
    event.contexts.trace.data.query.token = '(removed)'; // scrub token from telemetry
  }
  return event;
}

export function extractFrankieAuthSentryData(authToken: string) {
  const decodedToken = getToken<KycSessionToken>(authToken);
  return {
    frankieOneEntityId: decodedToken?.data?.entityId,
  };
}

export function extractImmersveAuthSentryData(authToken: string) {
  const { sid, sub } = jwtDecode<{ sub?: string; sid?: string }>(authToken);
  let cardholderAccountId: string | undefined;
  if (sub) {
    const cardholderAccountIdMatches = sub.match(
      /^irn:(?<cardholderAccountId>.+?):delegated-session:(?<sessionId>.+?)$/
    );

    cardholderAccountId = cardholderAccountIdMatches?.groups?.cardholderAccountId;
  }
  return {
    sid,
    cardholderAccountId,
  };
}

function tryToInitialiseSentryForImmersveKyc(scope: Scope) {
  //check if i'm on a session - use the Url
  const path = window.location.pathname;
  const kycSessionIdMatches = path.match(/^\/kyc\/session\/(?<sessionId>[^/.]+).*$/);
  if (!kycSessionIdMatches) {
    return;
  }
  const kycSessionId = kycSessionIdMatches.groups?.sessionId;

  if (!kycSessionId) {
    return;
  }

  scope.setTag('kycSessionId', kycSessionId);

  const sessionData = KycSessionStorage.getDataForSession<KycSessionData>(kycSessionId);
  const authToken = sessionData?.auth?.accessToken;

  if (!authToken) {
    //no point continuing as we require the auth token
    return;
  }

  const { cardholderAccountId, sid } = extractImmersveAuthSentryData(authToken);

  if (cardholderAccountId) {
    scope.setTag('cardholderAccountId', cardholderAccountId);
  }

  if (sid) {
    scope.setTag('sessionId', sid);
  }
}

export function initialScope(scope: Scope) {
  scope.setTag('liveness', config.testModeEnabled ? Liveness.TEST : Liveness.LIVE);

  tryToInitialiseSentryForImmersveKyc(scope);

  return scope;
}

export function beforeBreadcrumb(breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb | null {
  if (breadcrumb.data?.url?.includes('favicon')) {
    return null;
  }
  if (!hint) {
    return breadcrumb;
  }
  //https://github.com/getsentry/sentry-javascript/issues/1467#issuecomment-418387896
  if (breadcrumb.category === 'ui.click') {
    const target = hint?.event?.target;
    if (target?.dataset?.test) {
      breadcrumb.message = `[data-test='${target.dataset.test}']`;
    }
  } else if (breadcrumb.category === 'xhr') {
    breadcrumb = handleXhrBreadcrumb(breadcrumb, hint);
  }
  return breadcrumb;
}

function handleXhrBreadcrumb(breadcrumb: Breadcrumb, hint: BreadcrumbHint): Breadcrumb {
  const xhr = hint.xhr as XMLHttpRequest;
  const responseURL = new URL(xhr.responseURL.toLowerCase());
  const isFrankieFinancial = responseURL.hostname.endsWith('frankiefinancial.io');

  if (!xhr.response || !isFrankieFinancial) {
    return breadcrumb;
  }

  const contentType = xhr.getResponseHeader('Content-Type');
  if (!contentType || !contentType.includes('application/json')) {
    return breadcrumb;
  }

  const response = JSON.parse(xhr.response);
  if (response.requestId) {
    breadcrumb.data = breadcrumb.data || {};
    breadcrumb.data.requestId = response.requestId;
  }

  return breadcrumb;
}
